"use client";

import { useEffect, useState } from "react";

import { useActiveOrg } from "@/features/active-org/_context/ActiveOrgContext";

import { useEstimator } from "../_context/EstimatorContext";
import { useEstimatorSources } from "../_context/EstimatorSourcesContext";
import { placeRegion, type PlaceOption } from "../_logic/places";
import { cityKey } from "../_logic/workspace";

/**
 * Adds a destination the way the Where editor does (skipping one already compared, pinning the
 * picked airport), and offers the org's past cities that aren't compared yet as quick picks.
 * @returns `add`, and the quick picks (undefined until there are any).
 * @throws Never.
 */
export function useAddDestination(): {
  add: (place: PlaceOption) => void;
  quickPicks: { heading: string; options: PlaceOption[] } | undefined;
} {
  // Hooks
  const { workspace, edit } = useEstimator();
  const orgId = useActiveOrg().activeOrg?.id ?? null;
  const sources = useEstimatorSources();

  // States
  const [past, setPast] = useState<PlaceOption[]>([]);

  // Derived
  const compared = (city: string) => workspace.destinations.some((name) => cityKey(name) === cityKey(city));
  const options = past.filter((place) => !compared(place.city));
  const quickPicks = options.length ? { heading: "Your past cities", options } : undefined;

  // Handlers
  const add = (place: PlaceOption) => {
    if (compared(place.city)) return;
    edit({ type: "addDestination", city: place.city, region: placeRegion(place) }, "user");
    if (place.codes[0]) edit({ type: "setAirport", city: place.city, code: place.codes[0] }, "user");
  };

  // Effects
  useEffect(() => {
    let cancelled = false;
    void sources
      .pastDestinations(orgId)
      .catch(() => [])
      .then((places) => !cancelled && setPast(places));
    return () => {
      cancelled = true;
    };
  }, [orgId, sources]);

  return { add, quickPicks };
}
